import { Type } from 'class-transformer';
import {
  ArrayMaxSize,
  IsArray,
  IsBoolean,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Min,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import {
  LOT_STORAGE_SOURCES,
  type LotStorageSourceValue,
} from '../lot-storage/create-lot-storage-assignment.dto';

const MAX_DRAFT_ITEMS_UPDATE = 200;

/**
 * Wire-format DTO for `PATCH /delivery/delivery-notes/:id` item storage updates.
 *
 * The two storage fields form an at-most-one pair (XOR): a draft item points to
 * EITHER a piece of storage equipment OR a cleaning area, never both. Each field
 * is UUID-validated only when the other is absent via `@ValidateIf`, mirroring
 * {@link CreateLotStorageAssignmentDto}. The authoritative "not both" rejection
 * (400) is re-asserted server-side in `DeliveryService.updateDraftDeliveryNote`
 * so a both-set payload can never reach the FK write — defence in depth between
 * the DTO contract and the service validation.
 */
export class UpdateDraftItemStorageDto {
  @IsString()
  @IsNotEmpty()
  id!: string;

  @ValidateIf((dto: UpdateDraftItemStorageDto) => !dto.storageCleaningAreaId)
  @IsUUID()
  @IsOptional()
  storageEquipmentId?: string;

  @ValidateIf((dto: UpdateDraftItemStorageDto) => !dto.storageEquipmentId)
  @IsUUID()
  @IsOptional()
  storageCleaningAreaId?: string;

  @IsOptional()
  @IsIn(LOT_STORAGE_SOURCES)
  storageAssignmentSource?: LotStorageSourceValue;
}

/**
 * Wire-format DTO for `PATCH /delivery/delivery-notes/:id`. `date` stays a
 * string on the wire (form field) and is parsed to a Date in the controller,
 * matching the existing `createDraftDeliveryNote` / `addItem` controller
 * pattern. Storage `items` are validated as nested DTOs so a malformed UUID or
 * both-set pair is rejected with 400 at the API boundary instead of leaking
 * into an unhandled Prisma P2003 (500) on the FK write.
 */
export class UpdateDraftDeliveryNoteDto {
  @IsOptional()
  @IsString()
  supplierId?: string;

  @IsOptional()
  @IsString()
  documentNumber?: string;

  @IsOptional()
  @IsString()
  date?: string;

  @IsOptional()
  @IsNumber()
  temperature?: number | null;

  @IsOptional()
  @IsBoolean()
  vehicleHygiene?: boolean;

  @IsOptional()
  @IsBoolean()
  packagingIntact?: boolean;

  @IsOptional()
  @IsString()
  notes?: string | null;

  @IsOptional()
  @IsString()
  currentStep?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  itemsCount?: number;

  @IsOptional()
  @IsBoolean()
  allItemsValidated?: boolean;

  @IsOptional()
  @IsArray()
  @ArrayMaxSize(MAX_DRAFT_ITEMS_UPDATE)
  @ValidateNested({ each: true })
  @Type(() => UpdateDraftItemStorageDto)
  items?: UpdateDraftItemStorageDto[];
}
